import { Injectable } from '@angular/core';
import { collection, deleteDoc, deleteField, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, setDoc, startAfter, writeBatch } from 'firebase/firestore';
import {
    NARRATION_INDEX_CHUNK_SIZE,
    NARRATION_INDEX_META_ID,
    narrationChunkId,
    narrationEntryFor,
    narrationIndexPath,
    NarrationIndexMeta,
    groupNarrationEntries,
    StoredNarrationEntry,
    Transaction
} from 'bankapp-types';

/** A chunk entry unpacked for matching */
export interface MatchableEntry {
    index: number
    id: string
    narration: string
    /** Lowercased once at load, so matching doesn't re-lower every entry per search */
    lower: string
    date: number
    transaction: number
}

export interface LoadedNarrationIndex {
    entries: MatchableEntry[]
    /**
     * Highest transaction index the build covered. Anything above this was
     * ingested afterwards and has to be searched live, or results go stale.
     */
    coveredThrough: number
    builtAt: number
    /** Entries have been shifted between chunks; the index can't be trusted until rebuilt */
    needsRebuild: boolean
}

@Injectable({
    providedIn: 'root'
})
export class NarrationIndexService {

    private firestore = getFirestore()

    /** Transactions packed per chunk document. Firestore bills per document, so packing is what makes a search cheap. */
    chunkSize = NARRATION_INDEX_CHUNK_SIZE

    /** Transactions read per page while building */
    buildPageSize = 2000

    /** Firestore caps a batch at 500 operations */
    private maxBatchOps = 450

    /** Reserved document id holding the build metadata alongside the numeric chunks */
    private metaId = NARRATION_INDEX_META_ID

    /** Loaded indexes keyed by account and view, so repeat searches in a session cost nothing */
    private cache = new Map<string, LoadedNarrationIndex>()

    /**
     * Index metadata, cached so write paths don't re-read it for every transaction.
     * A null value is cached too: most accounts have no index, and rediscovering
     * that on every write would cost a read each time.
     */
    private metaCache = new Map<string, NarrationIndexMeta | null>()

    private indexPath(bankId: string, bpay: boolean) {
        return narrationIndexPath(bankId, bpay)
    }

    private transactionsPath(bankId: string, bpay: boolean) {
        return `bankAccounts/${bankId}/${bpay ? 'bpay-' : ''}transactions`
    }

    private cacheKey(bankId: string, bpay: boolean) {
        return `${bankId}:${bpay}`
    }

    chunkIdFor(transactionIndex: number) {
        return narrationChunkId(transactionIndex)
    }

    /** Drops the in-memory copies so the next search re-reads the chunks */
    invalidate(bankId?: string, bpay?: boolean) {
        if (bankId === undefined) {
            this.cache.clear()
            this.metaCache.clear()
            return
        }
        this.cache.delete(this.cacheKey(bankId, bpay))
        this.metaCache.delete(this.cacheKey(bankId, bpay))
    }

    private metaRef(bankId: string, bpay: boolean) {
        return doc(collection(this.firestore, this.indexPath(bankId, bpay)), this.metaId)
    }

    /** The index metadata, or null when the account has no index */
    async meta(bankId: string, bpay: boolean, force = false): Promise<NarrationIndexMeta | null> {
        const key = this.cacheKey(bankId, bpay)
        if (!force && this.metaCache.has(key)) return this.metaCache.get(key)

        const snapshot = await getDoc(this.metaRef(bankId, bpay))
        if (!snapshot.exists()) {
            this.metaCache.set(key, null)
            return null
        }
        const value = snapshot.data() as NarrationIndexMeta
        this.metaCache.set(key, value)
        return value
    }

    /** Whether the account has an index that write paths should keep up to date */
    async exists(bankId: string, bpay: boolean) {
        return !!(await this.meta(bankId, bpay))
    }

    /**
     * Reads every chunk and flattens it ready for matching. Returns null when no
     * index has been built for the account, which the caller treats as "fall back".
     */
    async load(bankId: string, bpay: boolean, force = false): Promise<LoadedNarrationIndex | null> {
        const key = this.cacheKey(bankId, bpay)
        if (!force && this.cache.has(key)) return this.cache.get(key)

        const { docs } = await getDocs(collection(this.firestore, this.indexPath(bankId, bpay)))
        if (!docs.length) return null

        const entries: MatchableEntry[] = []
        let coveredThrough = -1
        let builtAt = 0
        let needsRebuild = false

        for (const chunk of docs) {
            if (chunk.id === this.metaId) {
                const meta = chunk.data() as NarrationIndexMeta
                this.metaCache.set(key, meta)
                coveredThrough = meta.coveredThrough ?? -1
                builtAt = meta.builtAt ?? 0
                needsRebuild = !!meta.needsRebuild
                continue
            }
            const stored: Record<string, StoredNarrationEntry> = chunk.data().entries ?? {}
            for (const [id, entry] of Object.entries(stored)) {
                const narration = String(entry?.n ?? "")
                entries.push({
                    index: entry?.x ?? 0,
                    id,
                    narration,
                    lower: narration.toLowerCase(),
                    date: entry?.d ?? 0,
                    transaction: entry?.t ?? 0
                })
            }
        }

        // An index whose meta exists but holds no entries yet is empty, not absent;
        // saying "absent" here would disagree with exists() and strand the account
        if (!entries.length && !this.metaCache.get(key)) return null

        entries.sort((a, b) => b.index - a.index)
        // A missing or damaged meta doc shouldn't strand the index; the highest
        // entry is the honest floor for what was covered
        if (coveredThrough < 0 && entries.length) coveredThrough = entries[0].index

        const loaded = { entries, coveredThrough, builtAt, needsRebuild }
        this.cache.set(key, loaded)
        return loaded
    }

    /** True when the account holds transactions the index was built before */
    isStale(loaded: LoadedNarrationIndex, transactionCounter: number) {
        return (transactionCounter ?? 0) - 1 > loaded.coveredThrough
    }

    /**
     * Builds the index from scratch by reading every transaction on the account.
     * Chunks are replaced rather than merged, and chunks left over from a previous
     * build are deleted, so a rebuild can't leave phantom entries behind.
     */
    async build(bankId: string, bpay: boolean, onProgress?: (scanned: number) => void) {
        const transactions = collection(this.firestore, this.transactionsPath(bankId, bpay))
        const chunks = new Map<number, Record<string, StoredNarrationEntry>>()

        let cursor: number | undefined
        let scanned = 0
        let coveredThrough = -1

        // Ascending, so the cursor is a single number and progress only moves forward
        while (true) {
            const { docs } = await getDocs(query(transactions,
                orderBy("index"),
                ...(cursor === undefined ? [] : [startAfter(cursor)]),
                limit(this.buildPageSize)
            ))
            if (!docs.length) break

            for (const snapshot of docs) {
                const data = snapshot.data() as Transaction
                if (typeof data.index !== "number") continue
                const chunkId = this.chunkIdFor(data.index)
                if (!chunks.has(chunkId)) chunks.set(chunkId, {})
                chunks.get(chunkId)[snapshot.id] = narrationEntryFor(data)
                if (data.index > coveredThrough) coveredThrough = data.index
            }

            scanned += docs.length
            onProgress?.(scanned)

            cursor = (docs[docs.length - 1].data() as Transaction).index
            if (docs.length < this.buildPageSize) break
        }

        const indexCollection = collection(this.firestore, this.indexPath(bankId, bpay))
        const existing = await getDocs(indexCollection)
        const stale = existing.docs
            .filter(d => d.id !== this.metaId && !chunks.has(Number(d.id)))
            .map(d => d.id)

        let batch = writeBatch(this.firestore)
        let ops = 0
        const flush = async () => {
            if (!ops) return
            await batch.commit()
            batch = writeBatch(this.firestore)
            ops = 0
        }

        for (const [chunkId, entries] of chunks) {
            batch.set(doc(indexCollection, String(chunkId)), { entries })
            if (++ops >= this.maxBatchOps) await flush()
        }
        for (const chunkId of stale) {
            batch.delete(doc(indexCollection, chunkId))
            if (++ops >= this.maxBatchOps) await flush()
        }

        const meta: NarrationIndexMeta = {
            coveredThrough,
            chunkSize: this.chunkSize,
            entryCount: scanned,
            builtAt: Date.now(),
            needsRebuild: false
        }
        batch.set(doc(indexCollection, this.metaId), meta)
        ops++
        await flush()

        this.invalidate(bankId, bpay)
        this.metaCache.set(this.cacheKey(bankId, bpay), meta)
        return { scanned, chunks: chunks.size, removed: stale.length, coveredThrough }
    }

    /** Deletes every chunk, returning the account to the unindexed path */
    async clear(bankId: string, bpay: boolean) {
        const { docs } = await getDocs(collection(this.firestore, this.indexPath(bankId, bpay)))
        await Promise.all(docs.map(d => deleteDoc(d.ref)))
        this.invalidate(bankId, bpay)
        return docs.length
    }

    /**
     * Adds or replaces entries for transactions that were just written. Chunks are
     * merged, so the other entries sharing a chunk are untouched and no read is
     * needed first. Does nothing when the account has no index.
     */
    async upsert(bankId: string, bpay: boolean, written: Array<Partial<Transaction> & { index: number; id: string }>) {
        if (!written.length) return false

        const meta = await this.meta(bankId, bpay)
        if (!meta) return false

        const chunks = groupNarrationEntries(written.map(t => ({ id: t.id, entry: narrationEntryFor(t) })))
        const indexCollection = collection(this.firestore, this.indexPath(bankId, bpay))

        let batch = writeBatch(this.firestore)
        let ops = 0
        const flush = async () => {
            if (!ops) return
            await batch.commit()
            batch = writeBatch(this.firestore)
            ops = 0
        }

        for (const [chunkId, entries] of chunks) {
            batch.set(doc(indexCollection, String(chunkId)), { entries }, { merge: true })
            if (++ops >= this.maxBatchOps) await flush()
        }

        // coveredThrough only ever advances, or a late write would hide older entries
        const highest = written.reduce((max, t) => Math.max(max, t.index), meta.coveredThrough ?? -1)
        const nextMeta: NarrationIndexMeta = { ...meta, coveredThrough: highest }
        batch.set(doc(indexCollection, this.metaId), { coveredThrough: highest }, { merge: true })
        ops++
        await flush()

        this.cache.delete(this.cacheKey(bankId, bpay))
        this.metaCache.set(this.cacheKey(bankId, bpay), nextMeta)
        return true
    }

    /**
     * Removes entries for transactions that no longer exist. The index picks the
     * chunk, the document id picks the entry inside it.
     */
    async remove(bankId: string, bpay: boolean, removed: Array<{ index: number; id: string }>) {
        if (!removed.length) return false
        if (!await this.exists(bankId, bpay)) return false

        const byChunk = new Map<number, string[]>()
        for (const { index, id } of removed) {
            const chunkId = this.chunkIdFor(index)
            byChunk.set(chunkId, [...(byChunk.get(chunkId) ?? []), id])
        }

        const indexCollection = collection(this.firestore, this.indexPath(bankId, bpay))
        let batch = writeBatch(this.firestore)
        let ops = 0
        const flush = async () => {
            if (!ops) return
            await batch.commit()
            batch = writeBatch(this.firestore)
            ops = 0
        }

        for (const [chunkId, ids] of byChunk) {
            const entries = Object.fromEntries(ids.map(id => [id, deleteField()]))
            batch.set(doc(indexCollection, String(chunkId)), { entries }, { merge: true })
            if (++ops >= this.maxBatchOps) await flush()
        }
        await flush()

        this.cache.delete(this.cacheKey(bankId, bpay))
        return true
    }

    /**
     * Flags the index as untrustworthy. Reindexing shifts transactions between
     * chunks wholesale, which is as expensive to patch as it is to rebuild, so
     * searches fall back to scanning until someone rebuilds.
     */
    async markNeedsRebuild(bankId: string, bpay: boolean) {
        const meta = await this.meta(bankId, bpay)
        if (!meta) return false

        await setDoc(this.metaRef(bankId, bpay), { needsRebuild: true }, { merge: true })
        this.cache.delete(this.cacheKey(bankId, bpay))
        this.metaCache.set(this.cacheKey(bankId, bpay), { ...meta, needsRebuild: true })
        return true
    }

    /** Narrows entries to those whose narration contains any of the filter strings */
    matchNarration(entries: MatchableEntry[], narrationFilters: string[]) {
        if (!narrationFilters.length) return entries
        const needles = narrationFilters.map(n => n.toLowerCase())
        return entries.filter(e => needles.some(needle => e.lower.includes(needle)))
    }
}
